/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React, { useContext, useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import {
    productsByFilter as productsByFilter24,
    productsByCategoryUID,
    productSearch as productSearch24,
    categoryByFilterPagination
} from '../../queries/2.4.2ce';
import {
    categoryWithChildren,
    productsByFilter,
    productsByCategory,
    productWithVariants,
    productSearch
} from '../../queries/2.3.5';
import { trackEvent } from '../../utils/tracking';
import { event_productpicker_navigate, event_productpicker_search } from '../../utils/trackingEvents';
import { useGraphqlSchemaContext } from '../GraphqlSchemaContext/GraphqlSchemaContext';
import { useConfigContext } from '../ConfigContext/ConfigContext';
import { usePreviewDate } from '../common/usePreviewDate';
import { triggerWhenPickerRenders, triggerOnFilterChange, assignFolderBeenOpened } from '../../utils/pickerElement';

const SELECTION_VALUE_ID = 'id';
const SELECTION_VALUE_UID = 'uid';
const SELECTION_VALUE_SKU = 'sku';
const SELECTION_VALUE_SLUG = 'slug';
const SELECTION_VALUE_COMBINED_SKU = 'combinedSku';
const SELECTION_VALUE_COMBINED_SLUG = 'combinedSlug';
const INVENTORY_PAGINATION = {
    total: 0,
    page: 1,
    limit: 20
};

export const ProductPickerContext = React.createContext();

const asString = value => value && String(value);

const mapForInventory = ({ key, name, parentKey }) => ({
    key: asString(key),
    label: name,
    parentKey: asString(parentKey)
});

const computePath = (identifier, categoriesRef) => {
    let path = [];
    let currentItem = { parentKey: identifier };
    while (currentItem && currentItem.parentKey && currentItem.parentKey != null) {
        currentItem = categoriesRef.current.get(currentItem.parentKey);
        if (currentItem) path.unshift(currentItem);
    }

    return path;
};

export const buildFilterQuery = (keys, type) => {
    switch (type) {
        case SELECTION_VALUE_SLUG:
            return { url_key: { in: keys } };
        case SELECTION_VALUE_SKU:
            return { sku: { in: keys } };
        case SELECTION_VALUE_COMBINED_SKU:
            return { sku: { in: keys.map(i => i.split('#')[0]) } };
        case SELECTION_VALUE_COMBINED_SLUG:
            return { url_key: { in: keys.map(i => i.split('#')[0]) } };
        default:
            return { sku: { in: keys } };
    }
};

export const getSelectionId = (product, selectionId) => {
    const { slug, uid, id, sku, parentSku, unknown, label } = product;

    // If product is unknown, return the name as value
    if (unknown) {
        if (selectionId) {
            return [label, selectionId];
        }
        return [label, SELECTION_VALUE_SKU];
    }
    switch (selectionId) {
        case SELECTION_VALUE_SLUG:
            return [slug, SELECTION_VALUE_SLUG];
        case SELECTION_VALUE_UID:
            return [uid, SELECTION_VALUE_UID];
        case SELECTION_VALUE_ID:
            return [id + '', SELECTION_VALUE_ID];
        case SELECTION_VALUE_SKU:
            return [sku, SELECTION_VALUE_SKU];
        case SELECTION_VALUE_COMBINED_SKU:
            return [parentSku ? `${parentSku}#${sku}` : sku, SELECTION_VALUE_COMBINED_SKU];
        case SELECTION_VALUE_COMBINED_SLUG:
            return [parentSku ? `${slug}#${sku}` : slug, SELECTION_VALUE_COMBINED_SLUG];
        default:
            return [sku, SELECTION_VALUE_SKU];
    }
};

// i18n
const messages = defineMessages({
    rootCategory: {
        id: 'navigation.rootCategory',
        defaultMessage: 'Root Category'
    }
});

const getCategoryKey = (cat, gqlConfig) => {
    const { id, uid } = cat;
    if (gqlConfig.supportUID && uid) {
        return uid;
    } else {
        return String(id);
    }
};

const mapProduct = (item, selectionId) => {
    const { value, currency } = item.price_range.minimum_price.final_price;
    const mappedProduct = {
        sku: item.sku,
        name: item.name,
        label: item.name,
        image: item.thumbnail.url,
        type: item.__typename,
        price: value,
        currency,
        slug: item.url_key
    };

    if (item.id) {
        mappedProduct.id = item.id;
    }

    if (item.uid) {
        mappedProduct.uid = item.uid;
    }

    if (item.parentSku) {
        mappedProduct.parentSku = item.parentSku;
        mappedProduct.label = `${item.label} (${item.attributes.join(', ')})`;
    }

    mappedProduct.key = getSelectionId(mappedProduct, selectionId)[0];

    return mappedProduct;
};

const mapItemWithCaching = (product, selectionId, productsRef) => {
    const item = mapProduct(product, selectionId);
    productsRef.current.set(item.key, item);
    return item;
};

const ProductPickerContextProvider = ({
    selectionId,
    domRoot,
    webComponentTag,
    treeNavigationPagination = {
        total: 0,
        page: 1,
        limit: 20
    },
    children
}) => {
    const [[config]] = useConfigContext();
    const [schemaStatus, , [gqlConfig]] = useGraphqlSchemaContext();
    const [{ previewDate }, { setPreviewDate }] = usePreviewDate();
    const { formatMessage } = useIntl();
    const displayName = config.name ? config.name : formatMessage(messages.rootCategory);
    const rootCategory = { key: asString(config.rootCategory), name: displayName };
    const categoriesRef = useRef(new Map([[rootCategory.key, rootCategory]]));
    const productsRef = useRef(new Map());
    const [state, setState] = useReducer((o, n) => ({ ...o, ...n }), {
        items: [],
        pagination: INVENTORY_PAGINATION,
        path: [mapForInventory(rootCategory)],
        loadingState: 'idle'
    });
    const [filterState, setFilterState] = useState({});
    const [folderTreeState, setFolderTreeState] = useState({
        folders: undefined,
        isLoading: false,
        expandedFolders: []
    });

    /**
     * The method provides category data for the FolderTree component.
     */
    const onFolderExpand = async item => {
        let identifier = item.isRootFolder ? config.rootCategory : item.key;

        // Load root category if required
        await triggerOnFilterChange(domRoot, webComponentTag, onFilterChange, state.pagination);

        const children = (await getCategoriesForParent(identifier, treeNavigationPagination)) || [];
        if (children.length > 0) {
            // we don't need to track empty folders
            setFolderTreeState(old => ({
                expandedFolders: [...old.expandedFolders, identifier].filter((v, i, s) => s.indexOf(v) === i)
            }));
        }
        assignFolderBeenOpened(domRoot, webComponentTag);
        return children.map(mapForInventory);
    };

    /**
     * Internal method to retrieve child categories for a given parent category identifier.
     */
    const getCategoriesForParent = async (identifier, pagination) => {
        const gqlClient = gqlConfig.gqlClient;
        const variables = createQueryVariablesParentFolder(String(identifier), pagination);

        const result = await gqlClient.query({
            query: gqlConfig.supportUID ? categoryByFilterPagination : categoryWithChildren,
            variables
        });

        // Get children and add a key attribute
        let categories = [];

        // Getting categories depending on the query
        if (result) {
            // UID Support query
            if (gqlConfig.supportUID && result.data?.categories?.items && result.data.categories.items.length) {
                categories = result.data.categories.items;
            }
            // ID support query
            else if (
                result.data?.categoryList &&
                result.data.categoryList.length &&
                result.data.categoryList[0].children
            ) {
                categories = result.data.categoryList[0].children;
            } else if (result.data?.categoryList?.children && result.data.categoryList.children.length) {
                categories = result.data.categoryList.children;
            }
        }

        categories = categories.map(category => ({
            ...category,
            key: getCategoryKey(category, gqlConfig),
            parentKey: String(identifier)
        }));

        categories.forEach(child => categoriesRef.current.set(child.key, child));

        return categories;
    };

    const searchParentInCache = identifier => {
        if (categoriesRef.current.has(identifier)) {
            return categoriesRef.current.get(identifier).parentKey;
        }
        return '';
    };

    const createQueryVariablesParentFolder = (identifier, pagination) => {
        const variables = {};

        if (gqlConfig.supportUID) {
            variables['filters'] = {
                parent_category_uid: {
                    eq: identifier
                }
            };
            variables['pageSize'] = pagination.limit;
        } else {
            variables['id'] = [identifier];
        }

        return variables;
    };

    const createQueryVariablesExpandedFolders = (identifiers, pagination) => {
        const variables = {};

        if (gqlConfig.supportUID) {
            variables['filters'] = {
                parent_category_uid: {
                    in: identifiers
                }
            };
            variables['pageSize'] = pagination.limit;
        } else {
            variables['id'] = identifiers;
        }

        return variables;
    };

    /**
     * Internal method to retrieve child categories for given parent category identifiers.
     */
    const getCategoriesForExpandedFolders = async (identifiers, pagination) => {
        const stringIdentifiers = identifiers.map(id => String(id));
        const gqlClient = gqlConfig.gqlClient;
        const variables = createQueryVariablesExpandedFolders(stringIdentifiers, pagination);

        const result = await gqlClient.query({
            query: gqlConfig.supportUID ? categoryByFilterPagination : categoryWithChildren,
            variables
        });

        // Get children and add a key attribute
        let categories = [];

        // Getting categories depending on the query
        if (result) {
            // UID Support query
            if (gqlConfig.supportUID && result.data?.categories?.items && result.data.categories.items.length) {
                categories = result.data.categories.items;
            }
            // ID support query
            else if (result.data?.categoryList?.children && result.data.categoryList.children.length) {
                categories = result.data.categoryList.children;
            }
        }

        // Parent keys are searched in local cache
        categories = categories.map(category => {
            const key = getCategoryKey(category, gqlConfig);
            const parentKey = searchParentInCache(key);

            return {
                ...category,
                key,
                parentKey
            };
        });

        categories.forEach(child => categoriesRef.current.set(child.key, child));

        return categories;
    };

    /**
     * (Re)fetches the expanded folders and keeps them in the state
     */
    const reloadFolders = async () => {
        if (folderTreeState.expandedFolders.length > 0) {
            setFolderTreeState(old => ({ ...old, isLoading: true }));
            const allChildren = await getCategoriesForExpandedFolders(
                folderTreeState.expandedFolders,
                treeNavigationPagination
            );
            const allFolders = allChildren
                .map(mapForInventory)
                // replace the parentKey of the rootCategory with ''
                // we store it with the uid/id but the folder tree does not know that
                .map(folder => (folder.key === config.rootCategory ? { ...folder, parentKey: '' } : folder));
            setFolderTreeState(old => ({ ...old, folders: allFolders, isLoading: false }));
        }
    };
    useEffect(() => reloadFolders(), [previewDate]);

    const setPreviewDateInternal = async previewDate => {
        // apply the preview date globally
        // for some reason an empty set previewDate results in a string 'previewDate' being set
        await setPreviewDate(previewDate === 'previewDate' ? null : previewDate);
    };

    /**
     * The method provides product data for the Inventory component.
     */
    const onFilterChange = async ({ folder, filters = {}, pagination, query }, filterEvent) => {
        const identifier = folder ? folder : config.rootCategory;
        const resetPaginationEventKeys = new Set(['folder', 'query']);
        let { previewDate } = filters;
        let trackSearch = false;
        setFilterState({ query });

        // Don't allow navigating while loading in progress
        if (state.loadingState === 'loading') {
            return;
        }

        await setPreviewDateInternal(previewDate);

        setState({ items: [], loadingState: 'loading' });

        let items = [];
        let total_count = 0;
        let newPagination = pagination ? pagination : INVENTORY_PAGINATION;
        let newPath = Array.from(state.path);

        try {
            if (identifier.sku) {
                newPath.push({
                    key: identifier.key,
                    label: identifier.name,
                    parentKey: state.path[state.path.length - 1].key
                });

                ({ items, total_count } = await navigateVariants(identifier));

                newPagination = {
                    page: 1,
                    limit: total_count,
                    total: total_count
                };
            } else {
                // Compute new breadcrumb path
                newPath = computePath(String(identifier), categoriesRef).map(mapForInventory);
                if (
                    filterEvent &&
                    filterEvent.keys &&
                    filterEvent.keys.find(k => resetPaginationEventKeys.has(k.key))
                ) {
                    newPagination = INVENTORY_PAGINATION;
                    trackSearch = !!filterEvent.keys.find(k => k.key === 'query');
                }

                if (!previewDate && query && query !== '') {
                    ({ items, total_count } = await searchProducts(identifier, query, newPagination));
                } else {
                    ({ items, total_count } = await getProductsForCategory(identifier, newPagination));
                }
            }
        } catch (e) {
            console.error(e);
        }

        setState({
            items,
            pagination: { ...newPagination, total: total_count },
            loadingState: 'idle',
            path: newPath
        });

        if (trackSearch) {
            trackEvent(event_productpicker_search);
        } else {
            trackEvent(event_productpicker_navigate);
        }
    };

    const searchProducts = async (category, query, pagination) => {
        const gqlClient = gqlConfig.gqlClient;
        const variables = {
            searchTerm: query,
            page: pagination.page,
            pageSize: pagination.limit,
            sort: {
                name: 'ASC'
            }
        };

        if (category) {
            const categoryIdentifierType = gqlConfig.supportUID ? 'category_uid' : 'category_id';
            variables.filter = {
                [categoryIdentifierType]: {
                    eq: category
                }
            };
        }

        const result = await gqlClient.query({
            query: gqlConfig.supportUID ? productSearch24 : productSearch,
            variables
        });

        // Return early if result set is invalid or empty
        if (!result.data.products || result.data.products.total_count === 0) {
            return {
                items: [],
                total_count: 0
            };
        }

        // Get children and add a key attribute
        const { items, total_count } = result.data.products;

        return {
            items: items.map(p => mapItemWithCaching(p, selectionId, productsRef)),
            total_count
        };
    };

    /**
     * Internal method to retrieve products for a given parent category identifier.
     */
    const getProductsForCategory = async (identifier, pagination) => {
        const gqlClient = gqlConfig.gqlClient;
        const categoryIdentifierType = gqlConfig.supportUID ? 'category_uid' : 'category_id';
        const variables = {
            [categoryIdentifierType]: String(identifier),
            page: pagination.page,
            pageSize: pagination.limit,
            sort: {
                name: 'ASC'
            }
        };

        const result = await gqlClient.query({
            query: gqlConfig.supportUID ? productsByCategoryUID : productsByCategory,
            variables
        });

        // Return early if result set is invalid or empty
        if (!result.data.products || result.data.products.total_count === 0) {
            return {
                items: [],
                total_count: 0
            };
        }

        // Get children and add a key attribute
        const { items, total_count } = result.data.products;

        return {
            items: items.map(p => mapItemWithCaching(p, selectionId, productsRef)),
            total_count
        };
    };

    const getProducts = async identifiers => {
        let toBeQueried = new Map();
        let products = identifiers.map((identifier, idx) => {
            if (productsRef.current.has(identifier)) {
                return productsRef.current.get(identifier);
            }

            if (['id', 'uid'].includes(selectionId)) {
                return { key: identifier, label: identifier, unknown: true };
            }

            toBeQueried.set(identifier, idx);

            return { key: identifier, label: identifier, unknown: true };
        });

        // Deprecation notice
        if ([SELECTION_VALUE_ID, SELECTION_VALUE_UID].includes(selectionId)) {
            console.warn(
                '[Deprecation] The selection-id value of "%s" is deprecated. Please use "sku" instead.',
                selectionId
            );
        } else if (toBeQueried.size > 0) {
            const gqlClient = gqlConfig.gqlClient;
            const variables = {
                pageSize: toBeQueried.size,
                filter: buildFilterQuery(Array.from(toBeQueried.keys()), selectionId)
            };

            const result = await gqlClient.query({
                query: gqlConfig.supportUID ? productsByFilter24 : productsByFilter,
                variables
            });

            // Return early if result set is invalid or empty
            if (!result.data.products || result.data.products.total_count === 0) {
                return products;
            }

            // Get children and add a key attribute
            const { items } = result.data.products;

            if ([SELECTION_VALUE_COMBINED_SKU, SELECTION_VALUE_COMBINED_SLUG].includes(selectionId)) {
                mapCombinedIdentifiersItems(items, toBeQueried).forEach(({ idx, productInfo }) => {
                    products.splice(idx, 1, productInfo);
                });
            } else {
                items.forEach(item => {
                    const productInfo = mapItemWithCaching(item, selectionId, productsRef);
                    const idx = toBeQueried.get(productInfo.key);
                    products.splice(idx, 1, productInfo);
                });
            }
        }

        return products;
    };

    const mapCombinedIdentifiersItems = (items, queriedList) => {
        const mappedItems = new Map();
        const itemKey = selectionId === SELECTION_VALUE_COMBINED_SKU ? 'sku' : 'url_key';
        const results = [];
        items.forEach(item => {
            if (item.variants) {
                const variants = new Map();
                item.variants.forEach(v => {
                    variants.set(v.product.sku, { ...v.product, attributes: v.attributes.map(a => a.label) });
                });

                mappedItems.set(item[itemKey], { ...item, variants });
            } else {
                mappedItems.set(item[itemKey], item);
            }
        });

        queriedList.forEach((idx, key) => {
            const [parentKey, sku] = key.split('#');
            // Might be undefined for unknown items, return early if so
            const item = mappedItems.get(parentKey);
            if (!item) {
                return;
            }
            let productInfo;
            if (sku && item.variants && item.variants.has(sku)) {
                productInfo = mapItemWithCaching(
                    {
                        ...item.variants.get(sku),
                        parentSku: item.sku,
                        url_key: item.url_key,
                        label: item.name
                    },
                    selectionId,
                    productsRef
                );
            } else {
                productInfo = mapItemWithCaching(item, selectionId, productsRef);
            }
            results.push({
                idx,
                productInfo
            });
        });

        return results;
    };

    const navigateVariants = async product => {
        const gqlClient = gqlConfig.gqlClient;
        const variables = {
            sku: product.sku
        };

        const result = await gqlClient.query({
            query: productWithVariants,
            variables
        });

        // Return early if result set is invalid or empty
        if (!result.data.products || result.data.products.items.length === 0) {
            return {
                items: [],
                total_count: 0
            };
        }

        const { url_key, variants } = result.data.products.items[0];

        if (variants) {
            return {
                items: variants.map(p => {
                    const item = mapProduct(
                        {
                            ...p.product,
                            parentSku: product.sku,
                            url_key: url_key,
                            label: product.name,
                            attributes: p.attributes.map(a => a.label)
                        },
                        selectionId
                    );

                    productsRef.current.set(item.key, item);
                    return item;
                }),
                total_count: variants.length
            };
        }
    };

    useEffect(() => {
        if (schemaStatus !== 'done') {
            return;
        }

        // Reset the value when component gets rendered
        triggerWhenPickerRenders(domRoot, webComponentTag, onFilterChange, state.pagination);
    }, [schemaStatus]);

    const api = [
        {
            items: state.items,
            path: state.path,
            loadingState: state.loadingState,
            pagination: state.pagination,
            filters: {
                previewDate
            },
            filterState,
            folderTree: {
                loadingState: folderTreeState.isLoading ? 'loading' : 'idle',
                items: folderTreeState.folders
            }
        },
        {
            onFolderExpand,
            onFilterChange,
            getProducts,
            getSelectionId
        }
    ];

    return <ProductPickerContext.Provider value={api}>{children}</ProductPickerContext.Provider>;
};

ProductPickerContextProvider.propTypes = {
    selectionId: PropTypes.oneOf(['id', 'uid', 'sku', 'slug', 'combinedSku', 'combinedSlug']).isRequired,
    treeNavigationPagination: PropTypes.shape({
        total: PropTypes.number,
        page: PropTypes.number,
        limit: PropTypes.number
    }),
    webComponentTag: PropTypes.string,
    domRoot: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })])
};

ProductPickerContextProvider.defaultProps = {
    selectionId: 'sku'
};

export default ProductPickerContextProvider;

export const useProductPickerContext = () => useContext(ProductPickerContext);
